import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';

// No hardcoded fallback: a misconfigured build should fail admin auth loudly, not silently
// grant access via a value baked into source control.
const ADMIN_TOKEN = process.env.REACT_APP_ADMIN_TOKEN;

const getAdminHeaders = () => ({
  'X-Admin-Token': ADMIN_TOKEN
});

/**
 * Fetch all table names and their columns
 * Backend endpoint: GET /api/admin/db/tables
 * Expected response: { status: "success", data: { tables: [{ name: string, columns: [{ name, type, nullable }] }] } }
 */
export const fetchDbTables = createAsyncThunk('adminDb/fetchTables', async (_, { rejectWithValue }) => {
  try {
    const response = await axios.get('/api/admin/db/tables', {
      headers: getAdminHeaders()
    });
    if (response.data.status === 'success') {
      return response.data.data.tables;
    }
    return rejectWithValue(response.data.message || 'Failed to fetch tables');
  } catch (error) {
    return rejectWithValue(error.response?.data?.message || error.message);
  }
});

/**
 * Execute a raw SQL query (SELECT only for safety)
 * Backend endpoint: POST /api/admin/db/query
 * Body: { sql: string }
 * Expected response: { status: "success", data: { columns: string[], rows: any[][] } }
 */
export const executeQuery = createAsyncThunk('adminDb/executeQuery', async (sql, { rejectWithValue }) => {
  try {
    const response = await axios.post('/api/admin/db/query', { sql }, { headers: getAdminHeaders() });
    if (response.data.status === 'success') {
      return { columns: response.data.data.columns, rows: response.data.data.rows };
    }
    return rejectWithValue(response.data.message || 'Query failed');
  } catch (error) {
    return rejectWithValue(error.response?.data?.message || error.message);
  }
});

const adminDbSlice = createSlice({
  name: 'adminDb',
  initialState: {
    tables: [],
    tablesStatus: 'idle',
    tablesError: null,
    expandedTables: {},
    queryResult: null,
    queryStatus: 'idle',
    queryError: null
  },
  reducers: {
    toggleTable(state, action) {
      const name = action.payload;
      state.expandedTables[name] = !state.expandedTables[name];
    },
    clearQueryResult(state) {
      state.queryResult = null;
      state.queryError = null;
      state.queryStatus = 'idle';
    },
    clearQueryError(state) {
      state.queryError = null;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchDbTables.pending, (state) => {
        state.tablesStatus = 'loading';
        state.tablesError = null;
      })
      .addCase(fetchDbTables.fulfilled, (state, action) => {
        state.tablesStatus = 'succeeded';
        state.tables = action.payload;
      })
      .addCase(fetchDbTables.rejected, (state, action) => {
        state.tablesStatus = 'failed';
        state.tablesError = action.payload;
      })
      .addCase(executeQuery.pending, (state) => {
        state.queryStatus = 'loading';
        state.queryError = null;
        state.queryResult = null;
      })
      .addCase(executeQuery.fulfilled, (state, action) => {
        state.queryStatus = 'succeeded';
        state.queryResult = action.payload;
      })
      .addCase(executeQuery.rejected, (state, action) => {
        state.queryStatus = 'failed';
        state.queryError = action.payload;
      });
  }
});

export const { toggleTable, clearQueryResult, clearQueryError } = adminDbSlice.actions;
export default adminDbSlice.reducer;
